/**
 * Loans Page (Refactored)
 *
 * New Loan / Import → /loans/new (Loan Creation form).
 * Import hydrates the form via POST /api/loans/import/draft; loan is created
 * only through the normal Create Loan flow (POST /api/loans/full).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable } from '../components/common/DataTable';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useServerPagination } from '../hooks/useServerPagination';
import { exportCsv } from '@/lib/exportCsv';
import { formatCurrency } from '@/lib/formatters';
import { Plus, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '@/providers/AuthProvider';

// Components
import { LoansFilters } from '../components/loans/LoansFilters';
import { getLoansColumns } from '../components/loans/LoansTableConfig';

// Column definitions for the Loans Excel/CSV export. Shared so the export is a
// single source of truth regardless of which page the table is currently on.
const LOANS_EXPORT_COLUMNS = [
  { label: 'Loan #', key: 'loan_number' },
  { label: 'Sanction Date', key: 'sanction_date', format: (r) => r.sanction_date || '' },
  { label: 'Borrower', key: 'borrower_name' },
  { label: 'Product', key: 'product_name', format: (r) => r.product_name || '' },
  { label: 'Securities', key: 'securities_count', format: (r) => r.securities_count || 0 },
  { label: 'Sanctioned', key: 'sanctioned_amount', format: (r) => formatCurrency(r.sanctioned_amount) },
  { label: 'Outstanding', key: 'total_outstanding', format: (r) => formatCurrency(r.total_outstanding || 0) },
  { label: 'Status', key: 'status', format: (r) => (r.is_frozen ? 'Frozen' : r.status) },
  { label: 'Risk', key: 'npa_status', format: (r) => r.npa_status || '' },
  { label: 'DPD', key: 'days_past_due', format: (r) => r.days_past_due || 0 },
];

export default function Loans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canImport = !!(user?.is_tenant_admin || (user?.roles || []).includes('tenant_admin'));
  const [products, setProducts] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loanTypeFilter, setLoanTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const productsResult = await api.get('/api/loan-types');
      setProducts(productsResult || []);
    } catch (error) {
      toast.error('Failed to load loan products');
    }
  };

  // Server-side pagination fetch function
  const fetchLoans = useCallback(async (page, pageSize) => {
    const offset = (page - 1) * pageSize;
    const params = new URLSearchParams({ offset, limit: pageSize });
    
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (loanTypeFilter !== 'all') params.append('product_id', loanTypeFilter);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (minAmount) params.append('min_amount', minAmount);
    if (maxAmount) params.append('max_amount', maxAmount);

    const response = await api.get(`/api/loans?${params}`);
    return response; // Backend returns {data, total, offset, limit}
  }, [searchTerm, statusFilter, loanTypeFilter, dateFrom, dateTo, minAmount, maxAmount]);

  const { data: loans, pagination, loading, reload } = useServerPagination(
    fetchLoans,
    {
      dependencies: [], // fetchLoans already has the dependencies
    }
  );

  const [exporting, setExporting] = useState(false);
  const exportingRef = useRef(false);

  // Build the filter query params (without pagination) shared by the list and
  // the export, so the export always matches the currently applied filters.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (loanTypeFilter !== 'all') params.append('product_id', loanTypeFilter);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (minAmount) params.append('min_amount', minAmount);
    if (maxAmount) params.append('max_amount', maxAmount);
    return params;
  }, [searchTerm, statusFilter, loanTypeFilter, dateFrom, dateTo, minAmount, maxAmount]);

  // Fetch every loan matching the current filters across ALL pages, then export.
  // Server-side pagination means `loans` only holds the current page, so the
  // export must pull the full result set itself. The backend caps limit at
  // 10000 per request, so we page through until all rows are collected.
  const handleExportAll = useCallback(async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    const PAGE_LIMIT = 10000;
    try {
      const all = [];
      let offset = 0;
      let total = Infinity;
      while (offset < total) {
        const params = buildFilterParams();
        params.set('offset', String(offset));
        params.set('limit', String(PAGE_LIMIT));
        // eslint-disable-next-line no-await-in-loop
        const response = await api.get(`/api/loans?${params}`);
        const rows = response?.data || [];
        total = response?.total ?? rows.length;
        all.push(...rows);
        if (rows.length === 0) break; // safety: avoid an infinite loop
        offset += rows.length;
      }

      if (all.length === 0) {
        toast.info('No loans to export');
        return;
      }

      exportCsv('loans', LOANS_EXPORT_COLUMNS, all);
      toast.success(`Exported ${all.length} loan${all.length === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error(error.message || 'Failed to export loans');
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  }, [buildFilterParams]);

  const handleCreate = () => {
    navigate('/loans/new');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLoanTypeFilter('all');
    setDateFrom(null);
    setDateTo(null);
    setMinAmount('');
    setMaxAmount('');
  };

  const activeFiltersCount = [
    statusFilter !== 'all',
    loanTypeFilter !== 'all',
    dateFrom !== null,
    dateTo !== null,
    minAmount !== '',
    maxAmount !== '',
  ].filter(Boolean).length;

  return (
    <div data-testid="loans-page">
      <PageHeader
        title="Loans"
        description="Manage loan accounts and track repayments"
      >
        {canImport && (
          <Button
            variant="outline"
            onClick={() => navigate('/loans/new?import=1')}
            data-testid="loans-import-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
        )}
        <Button onClick={handleCreate} data-testid="loans-create-button">
          <Plus className="h-4 w-4 mr-2" />
          New Loan
        </Button>
      </PageHeader>
      <LoansFilters
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        loanTypeFilter={loanTypeFilter} setLoanTypeFilter={setLoanTypeFilter}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        minAmount={minAmount} setMinAmount={setMinAmount}
        maxAmount={maxAmount} setMaxAmount={setMaxAmount}
        showFilters={showFilters} setShowFilters={setShowFilters}
        activeFiltersCount={activeFiltersCount}
        onClearFilters={clearFilters}
        products={products}
        onExport={handleExportAll}
        exporting={exporting}
      />

      <DataTable
        columns={getLoansColumns(navigate)}
        data={loans}
        loading={loading}
        onRowClick={(row) => {
          if (row.is_creation_draft) {
            const draftId = row.draft_application_id || Math.abs(row.id);
            navigate(`/loans/new?draftId=${draftId}`);
            return;
          }
          navigate(`/loans/${row.id}`);
        }}
        hideSearch
        hideExport
        testId="loans-table"
        pagination={pagination}
        exporting={exporting}
        onExport={handleExportAll}
      />
    </div>
  );
}
